import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Button } from "@/components/ui/button";
import { CreditCard, AlertCircle, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface StripeStatus {
  hasAccount: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  startedAt: string | null;
  completedAt: string | null;
  restrictedReason: string | null;
  isFoundingMaker: boolean;
  foundingMakerExpiresAt: string | null;
}

export function StripeOnboardingBanner() {
  const [redirecting, setRedirecting] = useState(false);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery<StripeStatus>({
    queryKey: ["/api/seller/stripe-onboarding/status"],
    queryFn: async () => {
      const res = await fetch("/api/seller/stripe-onboarding/status", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch payment setup status");
      return res.json();
    },
    refetchOnWindowFocus: true,
  });

  // Fire a one-shot sync from Stripe whenever the banner mounts, regardless
  // of verified state. Catches both the onboarding-return path AND
  // post-verification restrictions (Stripe re-flagging an account for KYC,
  // expired bank info, etc.) — without this, a verified seller's banner
  // would never resync and we'd display stale "verified" state for hours.
  // Server throttles /sync to one stripe.accounts.retrieve per 60s per
  // account, so frequent dashboard mounts don't hammer Stripe.
  const [hasSynced, setHasSynced] = useState(false);
  useEffect(() => {
    if (hasSynced) return;
    if (!status?.hasAccount) return;
    setHasSynced(true);
    fetch("/api/seller/stripe-onboarding/sync", {
      method: "POST",
      credentials: "include",
    })
      .catch(() => {})
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/seller/stripe-onboarding/status"] });
        queryClient.invalidateQueries({ queryKey: ["/api/seller/stripe-balance"] });
        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          if (params.has("stripe")) {
            params.delete("stripe");
            const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
            window.history.replaceState({}, "", next);
          }
        }
      });
  }, [status?.hasAccount, hasSynced, queryClient]);

  const startOnboarding = async () => {
    try {
      setRedirecting(true);
      const endpoint = status?.hasAccount
        ? "/api/seller/stripe-onboarding/refresh"
        : "/api/seller/stripe-onboarding/start";
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        let message = "";
        try {
          const body = await res.json();
          message = typeof body?.message === "string" ? body.message : "";
        } catch {
          message = await res.text().catch(() => "");
        }
        throw new Error(message || `Stripe setup failed (${res.status})`);
      }
      const { url } = await res.json();
      if (url) {
        window.location.assign(url);
      } else {
        setRedirecting(false);
        toast({
          title: "Couldn't open Stripe",
          description: "We didn't get a setup link back. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      setRedirecting(false);
      toast({
        title: "Couldn't open Stripe",
        description: err?.message || "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  // A payout_nudge tap lands here with ?stripe=start — go straight to Stripe's
  // hosted onboarding rather than making the seller find the button. The link
  // has to be minted on arrival: Stripe account links expire in ~5 minutes, so
  // it can't be embedded in the notification itself. Read once on mount because
  // the sync effect above strips the param via replaceState.
  const [autoStart] = useState(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("stripe") === "start"
  );
  const [autoStartFired, setAutoStartFired] = useState(false);
  useEffect(() => {
    if (!autoStart || autoStartFired) return;
    if (!status || status.chargesEnabled) return;
    setAutoStartFired(true);
    void startOnboarding();
  }, [autoStart, autoStartFired, status]);

  if (isLoading || !status) return null;

  // Stripe's hosted dashboard blocks framing, so we open a one-time
  // login link in an in-app browser sheet on native (keeps the user in
  // the Stoop shell) and a new tab on web. The blank tab is opened
  // synchronously inside the click handler so popup blockers don't
  // intervene after the fetch await.
  const openDashboard = async () => {
    const newTab = Capacitor.isNativePlatform()
      ? null
      : window.open("about:blank", "_blank", "noopener,noreferrer");
    try {
      setOpeningDashboard(true);
      const res = await fetch("/api/seller/stripe-dashboard-link", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        let message = "";
        try {
          const body = await res.json();
          message = typeof body?.message === "string" ? body.message : "";
        } catch {
          message = await res.text().catch(() => "");
        }
        throw new Error(message || `Couldn't open Stripe (${res.status})`);
      }
      const { url } = await res.json();
      if (!url) throw new Error("No dashboard link returned");
      if (Capacitor.isNativePlatform()) {
        try {
          await Browser.open({ url });
        } catch {
          window.location.href = url;
        }
      } else if (newTab) {
        newTab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err: any) {
      newTab?.close();
      toast({
        title: "Couldn't open Stripe",
        description: err?.message || "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setOpeningDashboard(false);
    }
  };

  // Fully verified — show a quiet success row + Founding Maker badge if active.
  if (status.chargesEnabled && status.payoutsEnabled) {
    return (
      <div className="container mx-auto px-4 pt-3">
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-brand-secondary/10 px-3 py-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-brand-secondary" strokeWidth={2} />
          <span className="text-foreground">
            Payments verified — you can accept orders.
          </span>
          <div className="ml-auto flex items-center gap-2">
            {status.isFoundingMaker && (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                Founding Maker
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={openDashboard}
              disabled={openingDashboard}
              className="h-7 px-2 text-xs font-semibold text-foreground hover:bg-brand-secondary/20"
              data-testid="button-stripe-dashboard"
            >
              {openingDashboard ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  Stripe dashboard
                  <ExternalLink className="ml-1 h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Restricted — Stripe needs more info from the seller.
  if (status.restrictedReason) {
    return (
      <div className="container mx-auto px-4 pt-3">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground">Payment setup paused</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Stripe needs more information before you can accept orders.
                {status.restrictedReason ? ` Reason: ${status.restrictedReason}.` : ""}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={startOnboarding}
                disabled={redirecting}
                className="mt-3"
              >
                {redirecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening Stripe…
                  </>
                ) : (
                  "Continue setup with Stripe"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not started OR started-but-not-finished. Same CTA either way; the
  // backend's /start vs /refresh endpoint handles the difference.
  const isInProgress = status.hasAccount && !status.chargesEnabled;
  return (
    <div className="container mx-auto px-4 pt-3">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-brand-secondary/10 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-foreground">
              {isInProgress ? "Finish setting up payments" : "Set up payments to start selling"}
            </h3>
            <p className="mt-1 text-sm leading-snug text-muted-foreground">
              {isInProgress
                ? "You're almost there. Pick up right where you left off, about 2 minutes. Stripe handles it securely and Stoop never sees your bank details."
                : "Your listings are live, but buyers can only tap “Notify me” until payouts are on. Setup takes about 5 minutes: have your bank info handy, and Stripe (used by millions of businesses) handles it securely. Stoop never sees your bank details."}
            </p>
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
              <p>
                <span className="font-semibold text-foreground">1% seller fee.</span> We absorb Stripe's card processing — you keep 99% of your listed price.
              </p>
              <p>
                <span className="font-semibold text-foreground">Founding Maker eligible:</span> first 50 sellers get 0% fees for 6 months + a permanent badge.
              </p>
            </div>
            <Button
              type="button"
              onClick={startOnboarding}
              disabled={redirecting}
              className="mt-4"
              data-testid="button-stripe-onboarding-start"
            >
              {redirecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening Stripe…
                </>
              ) : isInProgress ? (
                "Continue with Stripe"
              ) : (
                "Set up payments"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
